import React, { useReducer } from "react";
import NewBusinessContext from "./newBusinessContext.jsx";
import NewBusinessReducer from "./newBusinessReducer.jsx";
import * as types from "./types";

import { ageUpdate } from "../../utilities/ageRangeMapping.js";
import { auraWordingList } from "../../data/auraExclusionWordingList.js";

//----------------------------------------------------------------

const NewBusinessState = (props) => {
  const initialData = {
    isDataUpdating: false,
  };

  const [state, dispatch] = useReducer(NewBusinessReducer, initialData);

  const dispatchWithCallback = (action, callback) => {
    dispatch(action);
    callback && callback();
  };

  const setIsUpdating = async () => {
    dispatchWithCallback({
      type: types.SET_IS_UPDATING,
    });
  };

  const setInitialData = async (outcomeData) => {
    await setIsUpdating();
    outcomeData.ageInformation = outcomeData?.underwritingQuestions?.dateOfBirth
      ? ageUpdate(
          {},
          new Date(outcomeData?.underwritingQuestions?.dateOfBirth),
          "online"
        )
      : null;

    await new Promise((resolve) => setTimeout(resolve, 0));

    dispatchWithCallback({
      type: types.SET_DATA_ON_INTERVIEW_OUTCOME,
      payload: outcomeData,
    });
  };

  //** Start: Update Covers, Loading and Exclusions */
  const updateCoverExclusionsData = async (newUpdateCoverData) => {
    await setIsUpdating();
    await new Promise((resolve) => setTimeout(resolve, 0));

    //? We have already mapped the values in the call to the
    //? updateConditions API.
    //? So, we can just pass the data through any update the state with this "new" data.

    dispatchWithCallback({
      type: types.UPDATE_DATA_ON_COVER_CHANGE,
      payload: newUpdateCoverData,
    });
  };

  //** End: Update Covers, Loading and Exclusions */

  const exclusionCodeMapping = (exclusionCodes, coverType) => {
    //* Match the code with the values returned from the eLife Aura data
    const exclusionWords = [];

    exclusionCodes.map((item) => {
      const auraItem = auraWordingList().find(
        (elm) =>
          elm.auraExclusionCode.trim() === item.trim() &&
          elm.riskTypeCode.trim() === coverType.trim()
      );
      if (auraItem != null) {
        exclusionWords.push(auraItem.exclusionWording);
      }
    });
    return exclusionWords;
  };

  const updateCoverData = async (newUpdateCoverData) => {
    //* Only the overall premiums and covers can get update
    //* so, we can just update them all and then, update
    //* the state
    await setIsUpdating();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const mappedValues = {
      ...state,
      quoteInformation: {
        ...state.quoteInformation,
        policyPremiumsTotal: {
          annual:
            newUpdateCoverData?.quoteInformation?.policyPremiumsTotal?.annual ||
            "",
          monthly:
            newUpdateCoverData?.quoteInformation?.policyPremiumsTotal
              ?.monthly || "",
        },
      },
      covers: {
        ...state.covers,
        hasLifeCover:
          newUpdateCoverData?.personOneQuoteInfo?.covers?.hasLifeCover,
        hasCriticalIllnessCover:
          newUpdateCoverData?.personOneQuoteInfo?.covers
            ?.hasCriticalIllnessCover,
        hasUnableToWorkCover:
          newUpdateCoverData?.personOneQuoteInfo?.covers?.hasUnableToWorkCover,
        hasCriticalIllnessUpSell:
          newUpdateCoverData?.personOneQuoteInfo?.covers
            ?.hasCriticalIllnessUpSell,
        life: {
          ...state.covers.life,
          sumInsured:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.life?.sumInsured ||
            "",
          annual:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.life?.annual || "",
          monthly:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.life?.monthly || "",
          loading:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.life?.loading || 0,
          totalLoading:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.life
              ?.totalLoading || 0,
          exclusionCodes: newUpdateCoverData?.personOneQuoteInfo?.covers?.life
            ?.exclusionCodes
            ? exclusionCodeMapping(
                newUpdateCoverData?.personOneQuoteInfo?.covers?.life
                  ?.exclusionCodes,
                "LIF"
              )
            : [],
          additionalExclusionCodes:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.life
              ?.additionalExclusionCodes || [],
        },
        criticalIllness: {
          ...state.covers.criticalIllness,
          sumInsured:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.criticalIllness
              ?.sumInsured || "",
          annual:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.criticalIllness
              ?.annual || "",
          monthly:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.criticalIllness
              ?.monthly || "",
          loading:
            newUpdateCoverData?.personOneQuoteInfo.covers?.criticalIllness
              ?.loading || 0,
          totalLoading:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.criticalIllness
              ?.totalLoading || 0,
          exclusionCodes: newUpdateCoverData?.personOneQuoteInfo?.covers
            ?.criticalIllness?.exclusionCodes
            ? exclusionCodeMapping(
                newUpdateCoverData?.personOneQuoteInfo?.covers?.criticalIllness
                  ?.exclusionCodes,
                "CI"
              )
            : [],
          traumaCover: newUpdateCoverData?.personOneQuoteInfo?.covers
            ?.hasLifeCover
            ? true
            : false,
          additionalExclusionCodes:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.criticalIllness
              ?.additionalExclusionCodes || [],
          ...(newUpdateCoverData?.personOneQuoteInfo?.covers
            ?.hasCriticalIllnessUpSell && {
            upSellSums: {
              premium:
                newUpdateCoverData?.personOneQuoteInfo?.covers?.upSellSums
                  ?.premium,
              monthly:
                newUpdateCoverData?.personOneQuoteInfo?.covers?.upSellSums
                  ?.monthly,
              annual:
                newUpdateCoverData?.personOneQuoteInfo?.covers?.upSellSums
                  ?.annual,
            },
          }),
        },
        unableToWork: {
          ...state.covers.unableToWork,
          sumInsured:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
              ?.sumInsured || "",
          annual:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
              ?.annual || "",
          monthly:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
              ?.monthly || "",
          loading:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
              ?.loading || 0,
          totalLoading:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
              ?.totalLoading || 0,
          exclusionCodes: newUpdateCoverData?.personOneQuoteInfo?.covers
            ?.unableToWork?.exclusionCodes
            ? exclusionCodeMapping(
                newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
                  ?.exclusionCodes,
                "PUW"
              )
            : [],
          additionalExclusionCodes:
            newUpdateCoverData?.personOneQuoteInfo?.covers?.unableToWork
              ?.additionalExclusionCodes || [],
        },
      },
    };
    dispatchWithCallback({
      type: types.UPDATE_DATA_ON_COVER_CHANGE,
      payload: mappedValues,
    });
  };

  const updateLifeCover = async (updateLifeCover) => {
    await setIsUpdating();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const mappedValues = {
      ...state,
      covers: {
        ...state.covers,
        life: {
          ...state.covers.life,
          sumInsured: updateLifeCover || "",
        },
      },
    };
    dispatchWithCallback({
      type: types.UPDATE_DATA_ON_COVER_CHANGE,
      payload: mappedValues,
    });
  };

  return (
    <NewBusinessContext.Provider
      value={{
        data: state,
        isDataUpdating: state.isDataUpdating,
        setInitialData, //* This is the data from the interview outcome
        updateCoverData,
        updateLifeCover,
        updateCoverExclusionsData,
      }}
    >
      {props.children}
    </NewBusinessContext.Provider>
  );
};

export default NewBusinessState;
